import { useState, useRef, useCallback, useEffect } from 'react';
import { getCompanyInfo, reportMissingData } from '../utils/newbackendcall';
import { useUser } from '../../../../context/UserContext';

// Cache for company data (shared across all hook instances)
const companyCache = new Map();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

export const useCompanyHover = (debounceMs = 300) => {
    const [hoveredCompany, setHoveredCompany] = useState(null);
    const [companyData, setCompanyData] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [hoverPosition, setHoverPosition] = useState({ x: 0, y: 0 });

    // Get user context for automation
    const { user } = useUser();

    const hoverTimeoutRef = useRef(null);
    const abortControllerRef = useRef(null);

    const handleMouseEnter = useCallback((companyCode, companyName, event) => {
        // Clear any existing timeout
        if (hoverTimeoutRef.current) {
            clearTimeout(hoverTimeoutRef.current);
        }

        // Calculate position for the hover card with viewport bounds check
        const rect = event.currentTarget.getBoundingClientRect();
        const cardWidth = 520; // Default card width from CSS (updated to match new width)
        const cardMaxHeight = 600; // Estimate max height for positioning (card now expands naturally)
        const gap = 0; // No gap to ensure mouse can move to card easily

        let x = rect.left;
        let y = rect.bottom + gap;

        // Check if card would overflow right edge of viewport
        if (rect.left + cardWidth > window.innerWidth) {
            // Position from right edge instead
            x = rect.right - cardWidth;
            // Ensure it doesn't overflow left edge
            if (x < 10) {
                x = 10; // 10px margin from left
            }
        }

        // Check if card would overflow bottom of viewport
        if (rect.bottom + cardMaxHeight + gap > window.innerHeight) {
            // Position above the element instead
            y = rect.top - gap - cardMaxHeight;
            // Ensure it doesn't overflow top
            if (y < 10) {
                y = 10; // 10px margin from top
            }
        }

        setHoverPosition({ x, y });

        // Set timeout for debouncing
        hoverTimeoutRef.current = setTimeout(async () => {
            setHoveredCompany({ code: companyCode, name: companyName });

            // Check cache first
            const cacheKey = companyCode || companyName;
            const cachedEntry = companyCache.get(cacheKey);

            if (cachedEntry && Date.now() - cachedEntry.timestamp < CACHE_DURATION) {
                setCompanyData(cachedEntry.data);
                setError(cachedEntry.error || null);
                setIsLoading(false);
                return;
            }

            // Fetch fresh data
            setIsLoading(true);
            setError(null);
            setCompanyData(null);

            // Cancel any in-flight request
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }

            abortControllerRef.current = new AbortController();

            try {
                const data = await getCompanyInfo({
                    companyCode: companyCode || companyName
                });

                // Check if data is empty or indicates error
                // Handle case: { result: { message: "..." }, status: 400 }
                // Handle case: { data: [], status: 404, message: "..." }
                const isError = !data ||
                    (Array.isArray(data.data) && data.data.length === 0) ||
                    data.status === 404 ||
                    data.status === 400;

                if (isError) {
                    const errorObj = {
                        message: data?.result?.message || data?.message || "Company not found.",
                        status: data?.status
                    };
                    setError(errorObj);
                    setCompanyData(null);

                    // Trigger automation if status is 400 (missing data)
                    if (data?.status === 400 && user) {
                        reportMissingData({
                            userFirstName: user.firstname || '',
                            userLastName: user.lastname || '',
                            userCompany: user.company_name_for_growth_iq || '',
                            searchedCompany: companyName
                        }).catch(err => console.error("Auto-report failed:", err));
                    }

                    // Cache the error response
                    companyCache.set(cacheKey, {
                        data: null,
                        error: errorObj,
                        timestamp: Date.now()
                    });
                } else if (data.status === 200 && data.result) {
                    // Success case: extract and map data from result field
                    const apiData = data.result;

                    // Check if this is an "empty" response (only has code/name and default logo)
                    // This indicates the company is in the system but data hasn't been populated yet
                    // const isDefaultLogo = apiData.logo && apiData.logo.includes('default_company_logo');
                    const isDefaultLogo = apiData.logo
                    const hasMinimalData = !apiData.website &&
                        !apiData.industry &&
                        !apiData.sector &&
                        !apiData.longBusinessSummary &&
                        !apiData.ticker &&
                        !apiData.exchange;

                    if (isDefaultLogo && hasMinimalData) {
                        // Treat this as a "pending data" error
                        const errorObj = {
                            message: "Thank you for your interest. Our analyst will update the data within 3-4 working days. We appreciate your patience.",
                            status: 400  // Use 400 to trigger the Knowledge Store error message
                        };
                        setError(errorObj);
                        setCompanyData(null);

                        // Trigger automation for minimal data case
                        if (user) {
                            reportMissingData({
                                userFirstName: user.firstname || '',
                                userLastName: user.lastname || '',
                                userCompany: user.company_name || user.company_name_for_growth_iq || '',
                                searchedCompany: companyName
                            }).catch(err => console.error("Auto-report (minimal data) failed:", err));
                        }
                        setCompanyData(null);

                        // Cache the error response
                        companyCache.set(cacheKey, {
                            data: null,
                            error: errorObj,
                            timestamp: Date.now()
                        });
                    } else {
                        // Map API field names to component field names
                        const mappedData = {
                            longName: apiData.name || apiData.longName,
                            logo: apiData.logo,
                            website: apiData.website,
                            symbol: apiData.ticker || apiData.symbol,
                            exchange: apiData.exchange,
                            industry: apiData.industry,
                            sector: apiData.sector,
                            fullTimeEmployees: typeof apiData.fullTimeEmployees === 'string'
                                ? parseInt(apiData.fullTimeEmployees.replace(/,/g, ''), 10)
                                : apiData.fullTimeEmployees,
                            marketCap: apiData.market_cap_bn
                                ? apiData.market_cap_bn * 1e9  // Convert billions to full number
                                : apiData.marketCap,
                            totalRevenue: apiData.totalRevenue
                                ? apiData.totalRevenue * 1e9  // Convert billions to full number
                                : undefined,
                            longBusinessSummary: apiData.longBusinessSummary,
                            city: apiData.city,
                            state: apiData.state,
                            country: apiData.country
                        };

                        setCompanyData(mappedData);
                        setError(null);

                        // Cache the successful response
                        companyCache.set(cacheKey, {
                            data: mappedData,
                            error: null,
                            timestamp: Date.now()
                        });
                    }
                } else {
                    // Fallback for other data formats
                    setCompanyData(data);
                    setError(null);

                    // Cache the successful response
                    companyCache.set(cacheKey, {
                        data: data,
                        error: null,
                        timestamp: Date.now()
                    });
                }
            } catch (err) {
                if (err.name !== 'AbortError') {
                    const errorObj = {
                        message: err.message || "Failed to fetch company information."
                    };
                    setError(errorObj);
                    setCompanyData(null);

                    // Cache the error
                    companyCache.set(cacheKey, {
                        data: null,
                        error: errorObj,
                        timestamp: Date.now()
                    });
                }
            } finally {
                setIsLoading(false);
            }
        }, debounceMs);
    }, [debounceMs]);

    const handleMouseLeave = useCallback(() => {
        // Clear the hover timeout if user leaves before debounce completes
        if (hoverTimeoutRef.current) {
            clearTimeout(hoverTimeoutRef.current);
            hoverTimeoutRef.current = null;
        }

        // Cancel any in-flight request
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
            abortControllerRef.current = null;
        }

        // Delay hiding to allow moving to the hover card
        // This timeout will be cleared if user hovers over the card
        const leaveTimeoutId = setTimeout(() => {
            setHoveredCompany(null);
            setCompanyData(null);
            setError(null);
            setIsLoading(false);
        }, 150);

        // Store timeout ID so it can be cleared from card's onMouseEnter
        hoverTimeoutRef.current = leaveTimeoutId;
    }, []);

    const handleCardMouseEnter = useCallback(() => {
        // Clear the leave timeout when hovering over the card
        if (hoverTimeoutRef.current) {
            clearTimeout(hoverTimeoutRef.current);
            hoverTimeoutRef.current = null;
        }
    }, []);

    const handleCardMouseLeave = useCallback(() => {
        // Delay hiding when leaving the card (allows moving back to company name)
        const leaveTimeoutId = setTimeout(() => {
            setHoveredCompany(null);
            setCompanyData(null);
            setError(null);
            setIsLoading(false);
        }, 150);

        // Store timeout ID so it can be cleared if user hovers back
        hoverTimeoutRef.current = leaveTimeoutId;
    }, []);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (hoverTimeoutRef.current) {
                clearTimeout(hoverTimeoutRef.current);
            }
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, []);

    return {
        hoveredCompany,
        companyData,
        isLoading,
        error,
        hoverPosition,
        handleMouseEnter,
        handleMouseLeave,
        handleCardMouseEnter,
        handleCardMouseLeave
    };
};

// Function to clear the cache (useful for testing or manual refresh)
export const clearCompanyCache = () => {
    companyCache.clear();
};
